import _ from "underscore";
import DriveView from "../../DriveView";
import { getVersion } from "../../utils/native/nativeContainer";
import { provideFeedback } from "../../utils/error";
import { isNoVehicle } from "../../utils/device";
import constants from "../../constants";
import { NetworkStates, PageLevel } from "../../store/mobile/types";
import UserHosRuleSet from "../../model/UserHosRuleSet";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import pageHTML from "./main.tmpl";
import { LANGUAGES } from "translator-tools";
import { setArrivedFromVersionTap } from "../debug/main";

export default class SettingsView extends DriveView<Backbone.Model> {
    private readonly template = _.template(pageHTML);
    private readonly databaseName: string | null;
    private myGeotabVersion: string;
    private currentUser: DeepReadonly<IUser> | null;
    private settings;
    private debugModeSteps = 0;
    private debugModeTimeout;
    private lastTapTime = 0;

    constructor (options) {
        super(options, {
            title: "Settings",
            activeSection: "settings",
            pageLevel: PageLevel.SUBMENU_WITH_BACK_BUTTON
        });
        this.myGeotabVersion = this.app.store.getState().data.clientVersion;
        this.databaseName = this.app.api.driveApi && this.app.api.driveApi.getCurrentCredentials()?.credentials?.database || (this.app.store.getState().data.lastDatabase ?? null);
        this.debugModeSteps = 0;

        this.listenTo(dispatcher, DispatcherEvents.UserUpdate, this.render.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.SystemSettingsUpdate, this.render.bind(this));
        this.storeUnsubscribe = this.app.observeStore(storeState => [storeState.data.clientVersion], () => {
            this.myGeotabVersion = this.app.store.getState().data.clientVersion;
            this.render();
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #changeLanguage": this.changeLanguage,
            "click #changeHomeTerminal": this.changeHomeTerminal,
            "click #changePassword": this.changePassword,
            "click #changeTimezone": this.changeTimezone,
            "click #myGeotabVersion": this.initiateDebugMode,
            "click #provideFeedback": this.provideFeedback
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        this.settings = this.app.store.getState().systemSettings;
        this.currentUser = this.app.userService.getCurrentUser();
        const canEditSettings = this.app.securityService.can("editUser") &&
            this.app.store.getState().mobile.online === NetworkStates.ONLINE;
        void this.display(this.template({
            user: this.currentUser,
            settings: this.settings || {},
            myGeotabVersion: this.myGeotabVersion.split("-")[0],
            androidAppVersion: getVersion(),
            canChangePassword: this.canUserChangePassword(),
            canEditUserSettings: canEditSettings,
            database: this.databaseName,
            language: (this.currentUser?.language && LANGUAGES.find(lang => lang.value === this.currentUser?.language)?.label) ?? "Unknown",
            showFeedback: !!constants.FEEDBACK.EXTERNAL_SUPPORT_URL
        }));
        return this;
    }

    changeHomeTerminal() {
        this.app.router.navigate("settings/homeTerminalChange", true);
    }

    changePassword () {
        let api = this.app.api;
        let iamService = this.app.context.iamService;
        let user = api.getUser();
        let username = "";

        if (user && user.iAMMetadata?.isIAMVerified) {
            api.getSession(null)
                .then(res => {
                    username = res.credentials.userName;
                    if (res.iamSettings) {
                        return iamService
                            .signInAndChangePassword(res.iamSettings, username);
                    }

                    return this.app.api
                        .serverCall(res.server, "GetUserRedirectInformation", {
                            database: res.credentials.database,
                            userName: username
                        });
                })
                .then(redirectUrlResult => {
                    if (!redirectUrlResult) {
                        return;
                    }

                    if (redirectUrlResult.informationType !== "IAM") {
                        this.app.router.navigate("settings/passwordChange", true);
                        return;
                    }

                    const iamSettings = {
                        iamProviderType: redirectUrlResult.iamProviderType,
                        loginUrl: redirectUrlResult.loginUrl,
                        clientId: redirectUrlResult.clientId,
                        language: this.app.translationService.getLanguage() || "en"
                    };

                    return iamService
                            .signInAndChangePassword(iamSettings, username);
                });
        } else {
            this.app.router.navigate("settings/passwordChange", true);
        }
    }

    changeLanguage() {
        this.app.router.navigate("settings/languageChange", true);
    }

    changeTimezone () {
        this.app.router.navigate("settings/timezoneChange", true);
    }

    changeRuleset() {
        this.app.router.navigate("/framework/ruleSetChangeView", true);
    }


    provideFeedback() { provideFeedback(); }

    getUserHosRuleSet(): UserHosRuleSet | undefined {
        return !(this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && isNoVehicle())) ? undefined : this.app.hosUtils.getLatestHosRuleSet();
    }

    remove () {
        clearTimeout(this.debugModeTimeout);
        super.remove();
        return this;
    }

    private initiateDebugMode () {
        // On iOS, rapid taps can produce multiple click events per physical tap.
        // Ignore events fired within 100ms of the previous one to prevent ghost clicks
        // from prematurely unlocking debug mode.
        const now = Date.now();
        if (now - this.lastTapTime < 100) {
            return;
        }
        this.lastTapTime = now;

        if (this.debugModeSteps > 8) {
            this.debugModeSteps = 0;
            setArrivedFromVersionTap();
            this.app.router.navigate("debug/main", true);
            return;
        }

        this.debugModeTimeout && clearTimeout(this.debugModeTimeout);

        this.debugModeTimeout = setTimeout(() => {
            this.debugModeSteps = 0;
        }, 5000);

        this.debugModeSteps++;
    }

    private canUserChangePassword(): boolean {
        return this.app.securityService.can("changePassword");
    }
}